/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

// Shared logic for both client and server implementations

import { isSameOrigin, isAbsolute } from "@cosmos/common-utils";
import { Document as XmlDocument, DOMParser as XmlDOMParser } from "@xmldom/xmldom";

const PAGES_IN_GAME = ["/tos", "/privacypolicy", "/fan-art-policy"];

// Core logic for processing anchors - shared between client and server
export const processAnchorsForGame = (dom: Document | XmlDocument, location: URL): void => {
	const anchors = dom.getElementsByTagName("a");
	const { length } = anchors;

	for (let i = 0; i < length; i++) {
		const anchor = anchors[i];
		const href = anchor.getAttribute("href");
		if (!href) continue;

		const isAbs = isAbsolute(href);
		const hrefObj = new URL(isAbs ? href : `${location.origin}${href}`);
		const isAnchor = isSameOrigin(hrefObj.toString()) && hrefObj.hash && hrefObj.hash.length > 1;
		if (isAnchor) continue;

		const isEpicUrl =
			hrefObj.hostname.indexOf("epicgames.com") !== -1 || hrefObj.hostname.indexOf("epicgames.net") !== -1;

		let isWhitelisted = false;
		if (isEpicUrl) {
			// only try to match paths if it's an epic url
			for (const pagePath of PAGES_IN_GAME) {
				if (hrefObj.pathname.indexOf(pagePath) !== -1) {
					isWhitelisted = true;
					break;
				}
			}
		}

		if (isEpicUrl && isWhitelisted) {
			// game page needs to process all links and add the request parameter to the privacy policy and fan content policy
			hrefObj.searchParams.set("mode", "game");
			anchor.setAttribute("href", hrefObj.toString());
		} else {
			// game page needs to "unlink" all other links on the page when mode=game by changing the <a> tags into <span> tags
			if (anchor?.parentNode) {
				const replacementElement = dom.createElement("span");
				let newContent = anchor.textContent;
				const href = anchor.getAttribute("href");
				if (href) {
					newContent += ` [${href}]`;
				}
				replacementElement.textContent = newContent;
				replacementElement.setAttribute("style", "opacity: 0.65;");
				// Type assertion needed because xmldom and native DOM have different Node interfaces
				(anchor.parentNode as any).insertBefore(replacementElement, anchor.nextSibling);
				anchor.setAttribute("style", "display: none;");
			}
		}
	}
};

/**
 * Remove the wrapper required for parsing
 * Replace <br> tags with closing bracket version to prevent hydration issues
 * @param html
 */
export const htmlCleanup = (html: string): string => {
	const newHtml = html
		.replace('<div xmlns="http://www.w3.org/1999/xhtml">', "")
		.replace(/<\/div>$/, "")
		.replace(/<br(?!\s*\/)>/g, "<br />");

	return newHtml;
};

export const cleanForGame = (location: URL, html: string): string => {
	try {
		//try to use native DOMParser, but this will fail on server
		const parser = new DOMParser();

		// cannot be parsed without a single wrapping element
		const dom = parser.parseFromString(`<div xmlns="http://www.w3.org/1999/xhtml">${html}</div>`, "text/html");
		processAnchorsForGame(dom, location);

		let newHtml = dom?.body?.innerHTML || dom?.documentElement?.innerHTML || "";
		newHtml = htmlCleanup(newHtml);

		return newHtml;
	} catch {
		//use XmlDomParser on server
		const parser = new XmlDOMParser();

		// cannot be parsed without a single wrapping element
		const dom = parser.parseFromString(`<div xmlns="http://www.w3.org/1999/xhtml">${html}</div>`, "text/html");
		processAnchorsForGame(dom, location);

		let newHtml = dom.toString();
		newHtml = htmlCleanup(newHtml);

		return newHtml;
	}
};
